import React, { FC } from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { ColorFunction } from '@styles/colors/types';
import AttachItemGlass from '@components/common/AttachItemGlass';
import { GlassButton } from '@components/common/glassButton';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import i18n from 'i18n-js';
import AttachButton, { AttachButtonProps } from '@components/common/attachButton';
import ColorWrapper from '@styles/colors';
import { IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';
import { useManageAttachItemsGlass } from './hooks/useManageAttachItemsGlass';

export interface AttachItemsGlassProps {
  image: ImageDocumentStoreItem;
  showMoreLinkConfig?: Pick<AttachButtonProps, 'topRelativeOffset' | 'leftRelativeOffset'> &
    Required<Pick<AttachButtonProps, 'shouldUseRelativeLeftPosition'>>;
  C: ColorFunction;
  limit?: number;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  contextKey?: string | null;
}

const AttachItemsGlass: FC<AttachItemsGlassProps> = ({
  image,
  C,
  limit = 3,
  showMoreLinkConfig = { shouldUseRelativeLeftPosition: true, topRelativeOffset: -(IMAGE_HANDLING_HEADER_HEIGHT / 2) },
  font = initialFont,
  scale = initialScale,
  contextKey: contextKeyFromProps,
}) => {
  const { linksToRender, moreLinksExist, onAttachToImageContext, currentImageContextLink, contextKey } = useManageAttachItemsGlass({
    image,
    limit,
    contextKey: contextKeyFromProps,
  });

  const gap = scale(18);
  const itemHeight = scale(28);

  const commonButtonStyle: StyleProp<ViewStyle> = {
    borderRadius: scale(SCREEN_BORDER_RADIUS),
    paddingHorizontal: scale(10),
    paddingVertical: scale(3),
    justifyContent: 'center',
    alignItems: 'center',
    height: itemHeight,
  };

  const renderShowMoreLinksElement = (onPressHandler: () => void) => (
    <GlassButton onPress={onPressHandler} style={[commonButtonStyle, { top: (gap + itemHeight) * limit, cursor: 'pointer' }]} scale={scale}>
      {() => (
        <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>
          {i18n.t('See all')} {image.links?.length}...
        </Text>
      )}
    </GlassButton>
  );

  return (
    <View>
      {linksToRender.map((l, index) => (
        <AttachItemGlass
          key={`${l.scopeId}-${l.scopeReferenceId}`}
          buttonStyle={[commonButtonStyle, { top: (gap + itemHeight) * index }]}
          C={C}
          link={l}
          font={font}
          scale={scale}
          contextKey={contextKey}
        />
      ))}
      {currentImageContextLink && (
        <AttachItemGlass
          onAttachButtonPress={() => onAttachToImageContext({ isFavorite: false })}
          onFavoriteIconPress={() => onAttachToImageContext({ isFavorite: true })}
          buttonStyle={[commonButtonStyle, { top: (gap + itemHeight) * (linksToRender.length) }]}
          C={C}
          link={currentImageContextLink}
          textStyle={{ color: C('Label/Light/Tertiary') }}
          iconConfig={{ icon: 'attach', color: C('Label/Light/Tertiary'), width: scale(20), height: scale(20) }}
          font={font}
          scale={scale}
          contextKey={contextKey}
        />
      )}
      {moreLinksExist && <AttachButton renderCustomButton={renderShowMoreLinksElement} C={C} {...showMoreLinkConfig} />}
    </View>
  );
};

export default ColorWrapper(AttachItemsGlass);
